/**
 * ZITELOU - Parrainage
 * Qui a parrainé qui, et le remboursement dû à chaque parrain (un seul par parrain).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Badge, Button, Loader, ConfirmModal } from '../components/Common';
import api from '../services/api';
import { formatDate } from '../utils';
import { toast } from 'react-toastify';

const STATUS_VARIANT = {
  pending: 'info',
  ready: 'warning',
  refunded: 'success',
  cancelled: 'none',
  manual: 'error',
};

const euros = (cents) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;

const Person = ({ person }) =>
  person ? (
    <div>
      <Link to={`/users/${person.id}`} className="font-medium text-blue-700 hover:underline">
        {person.name || person.email}
      </Link>
      <p className="text-xs text-gray-500">{person.email}</p>
    </div>
  ) : (
    <span className="text-gray-400">Compte supprimé</span>
  );

const Referrals = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toRefund, setToRefund] = useState(null);
  const [refunding, setRefunding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.getReferrals());
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirmRefund = async () => {
    setRefunding(true);
    try {
      const result = await api.refundReferral(toRefund.id);
      if (result.refunded) {
        toast.success(`${euros(toRefund.amountCents)} remboursés à ${toRefund.sponsor?.name || 'le parrain'}.`);
      } else {
        toast.error(`Remboursement impossible : ${result.reward?.note || 'voir la note'}. À traiter à la main.`);
      }
      setToRefund(null);
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRefunding(false);
    }
  };

  const columns = [
    { key: 'sponsor', label: 'Parrain', render: (value) => <Person person={value} /> },
    { key: 'referee', label: 'Filleul (a commandé)', render: (value) => <Person person={value} /> },
    {
      key: 'code',
      label: 'Code utilisé',
      render: (value) => <span className="font-mono text-sm">{value}</span>,
    },
    {
      key: 'statusLabel',
      label: 'Statut',
      render: (value, row) => (
        <div>
          <Badge variant={STATUS_VARIANT[row.status] || 'default'} size="sm">{value}</Badge>
          {row.note && <p className="text-xs text-gray-500 mt-1 max-w-xs">{row.note}</p>}
        </div>
      ),
    },
    {
      key: 'dueAt',
      label: 'Échéance',
      render: (value, row) =>
        row.status === 'refunded' ? (
          <span className="text-xs text-gray-600">Remboursé le {formatDate(row.refundedAt)}</span>
        ) : (
          formatDate(value)
        ),
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) =>
        row.status === 'ready' || row.status === 'manual' ? (
          <Button size="sm" onClick={() => setToRefund(row)}>
            Rembourser {euros(row.amountCents)}
          </Button>
        ) : null,
    },
  ];

  const sponsorColumns = [
    { key: 'rank', label: 'N°', render: (value) => <span className="text-gray-600">{value}</span> },
    { key: 'user', label: 'Client', render: (value) => <Person person={value} /> },
    { key: 'code', label: 'Code de parrainage', render: (value) => <span className="font-mono font-bold">{value}</span> },
    {
      key: 'subscriptionStatus',
      label: 'Abonnement',
      render: (value) => <span className="text-sm text-gray-700">{value || '—'}</span>,
    },
    {
      key: 'rewardStatus',
      label: 'Parrainage',
      render: (value) => (value ? <Badge variant={STATUS_VARIANT[value] || 'default'} size="sm">{value}</Badge> : <span className="text-gray-400">aucun filleul</span>),
    },
  ];

  if (loading && !data) {
    return <Loader />;
  }

  const settings = data?.settings;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Parrainage</h1>
        {settings && (
          <p className="text-gray-600 mt-1">
            {settings.sponsors} parrain(s) sur {settings.maxSponsors} places (les {settings.maxSponsors} premiers clients). Remboursement de{' '}
            <strong>{euros(settings.rewardCents)}</strong> au parrain, une seule fois, {settings.delayDays} jours après la commande du filleul
            (délai de rétractation). Remboursement{' '}
            <strong>{settings.autoRefund ? 'automatique' : 'avec validation : cliquez sur « Rembourser »'}</strong>.
          </p>
        )}
      </div>

      {data?.customersWithoutCode > 0 && settings.sponsors < settings.maxSponsors && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded text-orange-900 text-sm">
          <strong>{data.customersWithoutCode} client(s) payant(s) n'ont pas encore de code de parrainage</strong> alors qu'il reste des places.
          Les codes se donnent aux clients existants avec la commande <code>app:referral:assign-codes</code> (voir docs/parrainage.md).
        </div>
      )}

      <h2 className="text-lg font-bold text-gray-900">Remboursements dus aux parrains</h2>
      <Card padding="none">
        <Table
          columns={columns}
          data={data?.rewards || []}
          keyField="id"
          loading={loading}
          emptyMessage="Aucun parrainage pour l'instant"
        />
      </Card>

      <h2 className="text-lg font-bold text-gray-900">Clients qui ont un code de parrainage ({settings?.sponsors ?? 0} sur {settings?.maxSponsors ?? 100})</h2>
      <Card padding="none">
        <Table
          columns={sponsorColumns}
          data={data?.sponsors || []}
          keyField="rank"
          loading={loading}
          emptyMessage="Aucun client n'a encore de code"
        />
      </Card>

      <ConfirmModal
        isOpen={!!toRefund}
        onClose={() => setToRefund(null)}
        onConfirm={confirmRefund}
        loading={refunding}
        title="Rembourser le parrain ?"
        message={
          toRefund
            ? `Rembourser ${euros(toRefund.amountCents)} à ${toRefund.sponsor?.name || toRefund.sponsor?.email} sur le paiement de sa commande, pour le parrainage de ${toRefund.referee?.name || toRefund.referee?.email || 'son filleul'} ? Un seul remboursement est possible par parrain.`
            : ''
        }
        confirmText="Rembourser"
      />
    </div>
  );
};

export default Referrals;
